// src/containers/Trader_Form/backtestUI.js
//
// ═══════════════════════════════════════════════════════════════════════════
// Design system for the Backtesting page.
//
// Implements the "Cryptologics-AI Backtesting" handoff (Cryptologics Backtest.dc.html
// + README.md, design studio, 2026-08-09). Tokens and copy come from the handoff
// verbatim; the prototype is a `<x-dc>` template, so the markup is recreated here
// as plain presentational React on top of the tokens the rest of the app uses.
//
// These components hold NO state and know nothing about backtests. Everything
// that decides, fetches or mutates stays in Trader_Form.js — this file only
// decides how it looks. That split is deliberate: the page is 11k lines and the
// styling was previously inline at every call site, so a token change meant a
// find-and-replace across hundreds of literals.
//
// THE RULE THIS FILE EXISTS TO ENFORCE
// The page's job is to stop a user trusting a result that does not deserve it.
// The honesty card, FDR line, surface verdicts, mirrored-exit warning, the
// holdout-is-spent note, the robustness tile and the consequential Load confirm
// are load-bearing. They may be restyled; they may not be shortened into a bare
// badge, hidden behind a tooltip, or dropped. Components that carry that weight
// are marked LOAD-BEARING below and take their text as content, never as a
// truncatable prop.
// ═══════════════════════════════════════════════════════════════════════════

import React from 'react';
import { Box } from '@mui/material';

/* ── Tokens ─────────────────────────────────────────────────────────────────
   Shared 1:1 with the Portfolio / AutoTrader handoffs. Amber is not a warning
   colour here in the usual sense — it is the "caution / honesty" colour, and it
   appears wherever the page is telling the user to trust something less. */
export const BT = {
  bg: '#0a0c11', card: '#0f1218', inner: '#0b0e15', tile: '#070a0f',
  bright: '#f2f6ff', text: '#e8edf7', body: '#9aa6bd', muted: '#6f7c96',
  faint: '#5e6a82', label: '#7b88a2', soft: '#cdd6e8', dim: '#8593ad',
  accent: '#4d8fc7', accent2: '#3f6fb0', link: '#9cc3e4',
  positive: '#46b88a', negative: '#d97b72', amber: '#d9a441',
  hairline: 'rgba(255,255,255,.06)',
  hairline2: 'rgba(255,255,255,.07)',
  inputBd: 'rgba(255,255,255,.1)',
  sans: "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  // Radii: 14 cards / 10-12 inner / 8-9 inputs and chips.
  rCard: '14px', rInner: '12px', rPanel: '10px', rInput: '9px', rChip: '8px',
  primaryBg: 'linear-gradient(180deg,#3d5c7b,#33506b)',
  primaryFg: '#e7eef6',
  // The signature: an amber left edge on anything meaning-bearing. Keep it.
  cautionEdge: 'inset 2px 0 0 rgba(217,164,65,.5)',
};

const amberBg = 'rgba(217,164,65,.06)';
const amberBd = 'rgba(217,164,65,.3)';
const negBg = 'rgba(217,123,114,.07)';
const negBd = 'rgba(217,123,114,.32)';
const posBg = 'rgba(70,184,138,.08)';
const posBd = 'rgba(70,184,138,.3)';

/* ── Primitives ──────────────────────────────────────────────────────────── */

/** Uppercase tracked micro-label. 9.5–11px mono, the page's structural voice. */
export const Micro = ({ children, size = 9.5, color = BT.label, sx = {}, ...p }) => (
  <Box component="span" sx={{
    fontFamily: BT.mono, fontSize: `${size}px`, letterSpacing: '.1em',
    color, display: 'block', ...sx,
  }} {...p}>{children}</Box>
);

/** Mono value text with tabular figures, so columns of numbers line up. */
export const Num = ({ children, size = 12.5, color = BT.text, weight = 400, sx = {}, ...p }) => (
  <Box component="span" sx={{
    fontFamily: BT.mono, fontSize: `${size}px`, color, fontWeight: weight,
    fontVariantNumeric: 'tabular-nums', ...sx,
  }} {...p}>{children}</Box>
);

export const Card = ({ children, sx = {}, ...p }) => (
  <Box sx={{
    background: BT.card, border: `1px solid ${BT.hairline}`, borderRadius: BT.rCard,
    boxShadow: '0 1px 2px rgba(0,0,0,.22)', p: '22px', mb: '16px', ...sx,
  }} {...p}>{children}</Box>
);

export const Panel = ({ children, sx = {}, ...p }) => (
  <Box sx={{
    background: BT.inner, border: `1px solid ${BT.hairline2}`,
    borderRadius: BT.rPanel, p: '14px 16px', ...sx,
  }} {...p}>{children}</Box>
);

/** Primary action. One per section at most — the handoff's gradient button. */
export const PrimaryBtn = ({ children, icon, disabled, sx = {}, ...p }) => (
  <Box component="button" type="button" disabled={disabled} sx={{
    display: 'inline-flex', alignItems: 'center', gap: '9px',
    fontFamily: BT.sans, fontSize: '13.5px', fontWeight: 600,
    color: disabled ? BT.muted : BT.primaryFg,
    background: disabled ? 'rgba(255,255,255,.05)' : BT.primaryBg,
    border: 'none', padding: '11px 22px', borderRadius: BT.rInput,
    cursor: disabled ? 'default' : 'pointer', transition: 'transform .2s',
    '&:hover': disabled ? {} : { transform: 'translateY(-1px)' },
    '&:focus-visible': { outline: `2px solid ${BT.accent}`, outlineOffset: '2px' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
    ...sx,
  }} {...p}>{icon}{children}</Box>
);

export const GhostBtn = ({ children, disabled, sx = {}, ...p }) => (
  <Box component="button" type="button" disabled={disabled} sx={{
    fontFamily: BT.sans, fontSize: '13px', fontWeight: 600,
    color: disabled ? BT.muted : BT.soft, background: 'transparent',
    border: `1px solid ${BT.inputBd}`, padding: '10px 20px', borderRadius: BT.rInput,
    cursor: disabled ? 'default' : 'pointer',
    '&:hover': disabled ? {} : { borderColor: 'rgba(77,143,199,.5)', color: '#fff' },
    '&:focus-visible': { outline: `2px solid ${BT.accent}`, outlineOffset: '2px' },
    ...sx,
  }} {...p}>{children}</Box>
);

/** Segmented mono chip used for modes, presets and toggles. */
export const Chip = ({ children, active, onClick, title, sx = {} }) => (
  <Box component="button" type="button" onClick={onClick} title={title}
    aria-pressed={!!active}
    sx={{
      fontFamily: BT.mono, fontSize: '11px', fontWeight: 600, letterSpacing: '.06em',
      padding: '7px 14px', borderRadius: BT.rChip, cursor: 'pointer',
      color: active ? BT.bright : BT.muted,
      background: active ? 'rgba(77,143,199,.18)' : 'transparent',
      border: `1px solid ${active ? 'rgba(77,143,199,.5)' : 'rgba(255,255,255,.12)'}`,
      whiteSpace: 'nowrap',
      '&:focus-visible': { outline: `2px solid ${BT.accent}`, outlineOffset: '2px' },
      ...sx,
    }}>{children}</Box>
);

export const TextInput = ({ sx = {}, ...p }) => (
  <Box component="input" type="text" sx={{
    fontFamily: BT.mono, fontSize: '13px', color: BT.text, background: BT.inner,
    border: `1px solid ${BT.inputBd}`, borderRadius: BT.rInput,
    padding: '11px 13px', outline: 'none',
    '&:focus': { borderColor: 'rgba(77,143,199,.5)' },
    '&:disabled': { color: BT.faint },
    ...sx,
  }} {...p} />
);

/* ── Verdict pills ───────────────────────────────────────────────────────────
   LOAD-BEARING. These are the page's judgement, not decoration. `tone` maps to
   the honesty palette: amber = trust this less, green = this held up, red = this
   failed. A pill never appears without its explanation next to it. */
const TONES = {
  amber: { fg: BT.amber, bg: 'rgba(217,164,65,.1)', bd: 'rgba(217,164,65,.35)' },
  green: { fg: BT.positive, bg: 'rgba(70,184,138,.1)', bd: 'rgba(70,184,138,.32)' },
  red: { fg: BT.negative, bg: 'rgba(217,123,114,.1)', bd: 'rgba(217,123,114,.34)' },
  grey: { fg: BT.muted, bg: 'rgba(255,255,255,.04)', bd: 'rgba(255,255,255,.12)' },
  // The baseline's colour (handoff leaders data): informational, not judged.
  blue: { fg: BT.link, bg: 'rgba(77,143,199,.12)', bd: 'rgba(77,143,199,.35)' },
  solidAmber: { fg: '#0a0c11', bg: BT.amber, bd: BT.amber },
  solidGreen: { fg: '#0a0c11', bg: BT.positive, bd: BT.positive },
};

export const VerdictPill = ({ children, tone = 'grey', size = 9.5, sx = {} }) => {
  const t = TONES[tone] || TONES.grey;
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', fontFamily: BT.mono,
      fontSize: `${size}px`, fontWeight: 700, letterSpacing: '.06em',
      color: t.fg, background: t.bg, border: `1px solid ${t.bd}`,
      padding: '3px 9px', borderRadius: '6px', whiteSpace: 'nowrap', ...sx,
    }}>{children}</Box>
  );
};

/** Map a sweep surface verdict to a pill tone + label. */
export const surfaceTone = (verdict) => {
  const v = String(verdict || '').toLowerCase();
  if (v.includes('plateau')) return { tone: 'green', label: 'BROAD PLATEAU' };
  if (v.includes('spike')) return { tone: 'amber', label: 'LONE SPIKE' };
  if (v.includes('outside')) return { tone: 'amber', label: 'OUTSIDE RANGE' };
  if (v.includes('narrow')) return { tone: 'amber', label: 'NARROW PEAK' };
  if (v.includes('insufficient') || v.includes('no-signal')) return { tone: 'grey', label: 'NOT MEASURABLE' };
  return { tone: 'grey', label: String(verdict || 'UNKNOWN').toUpperCase() };
};

/** Map a leaderboard row to a verdict pill. Noise is the one that gates Load. */
export const rowVerdict = (row, summary) => {
  if (row?.isBase) return { tone: 'blue', label: 'BASELINE', noise: false };
  const trades = row?.summary?.holdoutTrades ?? row?.summary?.totalTrades ?? 0;
  if (trades < 20) return { tone: 'amber', label: 'TOO THIN', noise: true };
  const corr = summary?.trainHoldoutRankCorr;
  if (Number.isFinite(corr) && corr < 0.1) return { tone: 'amber', label: 'LIKELY NOISE', noise: true };
  const h = row?.summary?.holdoutReturnPct;
  if (!Number.isFinite(h) || h <= 0) return { tone: 'red', label: 'NO EDGE', noise: true };
  return { tone: 'green', label: 'HELD UP', noise: false };
};

/* ── Progress stepper ────────────────────────────────────────────────────────
   Replaces a bare phase string. The handoff is explicit: never show just
   "preparing". A user who cannot see movement assumes the job has hung — and
   before the queue fixes, sometimes it had. */
const PHASES = [
  { key: 'queued', label: 'QUEUED' },
  { key: 'preparing', label: 'PREPARING' },
  { key: 'screening', label: 'SCREENING' },
  { key: 'backtesting', label: 'BACKTESTING' },
];

// The risk grid has no screen/backtest split — every cell is a direct
// simulation, reported as one 'sweeping' phase. That phase was missing from
// PHASES entirely, so findIndex returned -1, the clamp turned it into 0, and a
// risk sweep displayed QUEUED from first cell to last. The steps must match
// the phases the runner actually emits, which differ by kind.
export const RISK_PHASES = [
  { key: 'queued', label: 'QUEUED' },
  { key: 'preparing', label: 'PREPARING' },
  { key: 'sweeping', label: 'SIMULATING' },
];

export const ProgressStepper = ({ phase, progress, etaSeconds, pct, steps = PHASES }) => {
  const cur = Math.max(0, steps.findIndex(p => p.key === String(phase || '').toLowerCase()));
  const width = Number.isFinite(pct) ? `${Math.max(2, Math.min(100, pct))}%`
    : `${Math.max(2, Math.min(100, ((cur + 1) / steps.length) * 100))}%`;
  return (
    <Box sx={{
      mt: '16px', background: BT.inner, border: '1px solid rgba(77,143,199,.25)',
      borderRadius: BT.rPanel, p: '16px 18px',
    }} role="status" aria-live="polite">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mb: '12px', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '22px', flexWrap: 'wrap' }}>
          {steps.map((p, i) => {
            const done = i < cur, now = i === cur;
            return (
              <Num key={p.key} size={11} color={done ? BT.positive : now ? BT.bright : BT.faint}>
                {done ? '✓' : now ? '●' : '○'} {p.label}
                {/* The N/M counter rides whichever step is the long one. */}
                {now && (p.key === 'screening' || p.key === 'sweeping') && progress ? ` ${progress}` : ''}
              </Num>
            );
          })}
        </Box>
        {Number.isFinite(etaSeconds) && etaSeconds > 0 && (
          <Num size={11} color={BT.dim}>~{Math.round(etaSeconds)}s remaining</Num>
        )}
      </Box>
      {/* The bar is progress, not decoration, so it keeps its value under
          reduced-motion — only the animating transition is dropped. */}
      <Box sx={{ height: '7px', borderRadius: '4px', background: 'rgba(255,255,255,.06)', overflow: 'hidden' }}
        role="progressbar" aria-valuenow={Math.round(parseFloat(width))} aria-valuemin={0} aria-valuemax={100}>
        <Box sx={{
          width, height: '100%', borderRadius: '4px',
          background: 'linear-gradient(90deg,#3f6fb0,#4d8fc7)',
          transition: 'width .3s ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }} />
      </Box>
    </Box>
  );
};

/* ── Honesty card ────────────────────────────────────────────────────────────
   LOAD-BEARING, and the reason the page exists. Renders ABOVE the leaderboard,
   always, even when the news is good — a caveat that only appears on bad runs
   teaches users to read its absence as approval. `notes` are rendered in full:
   there is no truncation, no "show more". */
export const HonestyCard = ({ title = 'HOW MUCH OF THIS IS REAL', pill, pillTone = 'solidAmber', stats, notes = [], children, sx = {} }) => (
  <Box sx={{
    mt: '18px', background: BT.inner, border: `1px solid ${amberBd}`,
    borderRadius: BT.rInner, p: '18px', boxShadow: BT.cautionEdge, ...sx,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', mb: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <Micro size={10.5} sx={{ display: 'inline-block' }}>{title}</Micro>
        {pill && <VerdictPill tone={pillTone} size={10} sx={{ padding: '4px 10px' }}>{pill}</VerdictPill>}
      </Box>
      {stats && <Num size={11} color={BT.dim}>{stats}</Num>}
    </Box>
    {children}
    {!!notes.length && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {notes.map((n, i) => (
          <Box key={i} sx={{ display: 'flex', gap: '9px', fontSize: '12.5px', color: BT.body, lineHeight: 1.55 }}>
            <Box component="span" sx={{ color: BT.amber, flex: 'none' }}>·</Box>
            <Box component="span">{n}</Box>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

/* ── Trust banner ────────────────────────────────────────────────────────────
   LOAD-BEARING. Sits at the TOP of a result, before any headline number, so the
   caveat is read before the return rather than after it. */
export const TrustBanner = ({ pill, tone = 'amber', text, sx = {} }) => {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '13px', flexWrap: 'wrap',
      background: tone === 'green' ? posBg : tone === 'red' ? negBg : amberBg,
      border: `1px solid ${tone === 'green' ? posBd : tone === 'red' ? negBd : amberBd}`,
      borderRadius: BT.rInner, p: '13px 16px', mb: '16px',
      boxShadow: tone === 'amber' ? BT.cautionEdge : 'none', ...sx,
    }}>
      {pill && <VerdictPill tone={tone === 'amber' ? 'solidAmber' : tone} size={10} sx={{ padding: '4px 10px' }}>{pill}</VerdictPill>}
      <Box component="span" sx={{ fontSize: '13px', color: BT.soft, flex: 1, minWidth: '220px' }}>{text}</Box>
    </Box>
  );
};

/* ── Metrics band ────────────────────────────────────────────────────────────
   One bordered strip divided into sections, NOT equal tiles. Robustness is
   deliberately visually distinct: it is the section that says whether to believe
   the other three, and at equal weight nobody reads it that way. */
export const MetricsBand = ({ children, sx = {} }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
    background: BT.tile, border: `1px solid ${BT.hairline}`, borderRadius: BT.rInner,
    overflow: 'hidden', mb: '16px', ...sx,
  }}>{children}</Box>
);

export const MetricCell = ({ label, value, valueColor = BT.bright, sub, caution, children, sx = {} }) => (
  <Box sx={{
    p: '16px 18px', borderRight: { lg: `1px solid ${BT.hairline}` },
    borderBottom: { xs: `1px solid ${BT.hairline}`, lg: 'none' },
    '&:last-of-type': { borderRight: 'none', borderBottom: 'none' },
    background: caution ? 'rgba(217,164,65,.05)' : 'transparent',
    boxShadow: caution ? BT.cautionEdge : 'none',
    ...sx,
  }}>
    <Micro>{label}</Micro>
    {value !== undefined && (
      <Num size={22} color={valueColor} sx={{ display: 'block', mt: '7px', letterSpacing: '-.01em' }}>{value}</Num>
    )}
    {sub && <Box sx={{ fontFamily: BT.mono, fontSize: '10.5px', color: BT.muted, mt: '6px', lineHeight: 1.45 }}>{sub}</Box>}
    {children}
  </Box>
);

/** Thin proportion bar used under Max DD and the win/loss split. */
export const SplitBar = ({ segments = [], sx = {} }) => {
  const total = segments.reduce((a, s) => a + (Number(s.value) || 0), 0) || 1;
  return (
    <Box sx={{ display: 'flex', height: '5px', borderRadius: '3px', overflow: 'hidden', background: 'rgba(255,255,255,.06)', mt: '8px', ...sx }}>
      {segments.map((s, i) => (
        <Box key={i} sx={{ width: `${((Number(s.value) || 0) / total) * 100}%`, background: s.color }} />
      ))}
    </Box>
  );
};

/* ── Consequential load ──────────────────────────────────────────────────────
   LOAD-BEARING. Loading a noise-flagged variant into a bot draft is one click
   from real money, so it asks. The friction is the point; do not "streamline"
   this away. */
export const ConfirmBar = ({ text, confirmLabel = 'Load anyway', onCancel, onConfirm, sx = {} }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
    mt: '12px', p: '13px 16px', background: negBg,
    border: `1px solid ${negBd}`, borderRadius: BT.rPanel, ...sx,
  }} role="alertdialog" aria-live="assertive">
    <Box component="span" sx={{ color: BT.negative, flex: 'none', fontSize: '17px', lineHeight: 1 }}>⚠</Box>
    <Box component="span" sx={{ flex: 1, minWidth: '220px', fontSize: '13px', color: BT.soft }}>{text}</Box>
    <Box sx={{ display: 'flex', gap: '8px' }}>
      <GhostBtn onClick={onCancel} sx={{ fontSize: '12.5px', padding: '8px 16px' }}>Cancel</GhostBtn>
      <Box component="button" type="button" onClick={onConfirm} sx={{
        fontFamily: BT.sans, fontSize: '12.5px', fontWeight: 600, color: '#e9b3ad',
        background: 'rgba(217,123,114,.14)', border: '1px solid rgba(217,123,114,.45)',
        padding: '8px 16px', borderRadius: BT.rChip, cursor: 'pointer',
        '&:focus-visible': { outline: `2px solid ${BT.negative}`, outlineOffset: '2px' },
      }}>{confirmLabel}</Box>
    </Box>
  </Box>
);

export const SuccessNote = ({ children, sx = {} }) => (
  <Box sx={{
    mt: '12px', p: '11px 16px', background: posBg, border: `1px solid ${posBd}`,
    borderRadius: BT.rPanel, fontSize: '13px', color: '#7fd4ae', ...sx,
  }}>✓ {children}</Box>
);

/* ── Empty & failure states ──────────────────────────────────────────────────
   The handoff treats these as designed states, not afterthoughts: they are what
   a new user hits first. Both say what happened AND what to do next. */
export const Notice = ({ tone = 'amber', title, children, action, sx = {} }) => {
  const isErr = tone === 'red';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: '13px',
      background: isErr ? negBg : amberBg,
      border: `1px solid ${isErr ? negBd : 'rgba(217,164,65,.28)'}`,
      borderRadius: BT.rInner, p: '18px', ...sx,
    }}>
      <Box sx={{
        width: '34px', height: '34px', borderRadius: '9px', flex: 'none',
        background: isErr ? 'rgba(217,123,114,.14)' : 'rgba(217,164,65,.14)',
        border: `1px solid ${isErr ? 'rgba(217,123,114,.3)' : 'rgba(217,164,65,.3)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: isErr ? BT.negative : BT.amber, fontSize: '17px',
      }}>{isErr ? '!' : 'i'}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {title && (
          <Box sx={{ fontSize: '14.5px', fontWeight: 700, color: isErr ? '#efb7b0' : '#f0d9a8', mb: '5px' }}>{title}</Box>
        )}
        <Box sx={{ fontSize: '13px', color: BT.body, lineHeight: 1.55, wordBreak: 'break-word' }}>{children}</Box>
        {action && <Box sx={{ mt: '11px' }}>{action}</Box>}
      </Box>
    </Box>
  );
};

/* ── Surface cell strip ──────────────────────────────────────────────────────
   Zero-anchored colour: the sign is the point. Base carries a soft border, best
   a bright one, so "where you are" and "where the max is" are both legible
   without a legend. */
export const SurfaceCells = ({ points = [], bestValue, unit = '' }) => {
  const scores = points.map(p => p.score).filter(v => Number.isFinite(v));
  const mag = Math.max(1e-9, ...scores.map(Math.abs));
  return (
    <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap', mb: '11px' }}>
      {points.map((p) => {
        const v = Number.isFinite(p.score) ? p.score : null;
        const bg = v === null ? 'rgba(255,255,255,.04)'
          : v >= 0 ? `rgba(70,184,138,${0.12 + 0.5 * (v / mag)})`
            : `rgba(217,123,114,${0.12 + 0.5 * (Math.abs(v) / mag)})`;
        const isBest = p.value === bestValue;
        return (
          <Box key={String(p.value)} sx={{
            minWidth: '60px', padding: '7px 8px', borderRadius: '7px', textAlign: 'center',
            background: bg,
            border: `1px solid ${isBest ? 'rgba(242,246,255,.55)' : p.isBase ? 'rgba(255,255,255,.22)' : 'transparent'}`,
          }}>
            <Num size={12} color={BT.bright} sx={{ display: 'block' }}>{p.value}{unit}</Num>
            <Num size={10} color={BT.body} sx={{ display: 'block', mt: '2px' }}>
              {v === null ? '—' : v.toFixed(2)}
            </Num>
          </Box>
        );
      })}
    </Box>
  );
};

/* ── Leaderboard layout ──────────────────────────────────────────────────────
   The prototype is desktop-only (≈1180px) and the handoff lists responsive as
   still to build. Six fixed columns is the part that breaks worst: below about
   900px the variant name and the verdict pill collide, and the verdict is the
   column carrying the judgement.

   So it reflows rather than scrolls. A horizontally scrolling table hides the
   verdict off-screen, which for this page is the same as deleting it. */
export const LB_COLUMNS = {
  xs: '22px 1fr auto',
  md: '26px 1.7fr 0.8fr 0.7fr 1fr 0.9fr',
};

/** Columns that fold away on narrow screens, shown inline under the name instead. */
export const LbHideSmall = ({ children, sx = {} }) => (
  <Box sx={{ display: { xs: 'none', md: 'block' }, ...sx }}>{children}</Box>
);

/* ── Section heading ─────────────────────────────────────────────────────────
   The page is a sequence — configure, judge, tune — so numbered steps encode
   something true rather than decorating. */
export const StepHeading = ({ step, title, right, sx = {} }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', mb: '6px', ...sx }}>
    <Micro size={10.5} color={BT.soft} sx={{ display: 'inline-block' }}>
      {step ? `STEP ${step} · ` : ''}{title}
    </Micro>
    {right}
  </Box>
);

export default BT;
